import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { globalErrorHandler } from '../../../utils/GlobalErrorHandler';

const GuestRequests = () => {
    const navigate = useNavigate();
    const debounceTimeout = useRef(null);

    const [guestRequests, setGuestRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
    const [filter, setFilter] = useState({ name: '' });

    const getGuestRequests = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-guest-requests`, {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    name: filter.name
                },
                withCredentials: true
            });

            if (res.data.status) {
                setGuestRequests(res.data.guests || []);
                setPagination(res.data.pagination);
            } else {
                setGuestRequests([]);
            }
        } catch (error) {
            globalErrorHandler(error, navigate);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            getGuestRequests();
        }, 500);
        return () => clearTimeout(debounceTimeout.current);
    }, [pagination.page, filter]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilter(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // reset to page 1 on filter change
    };

    const clearFilter = () => {
        setFilter({ name: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleApprove = async (id) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/approve-guest`, { id }, { withCredentials: true });
            if (res.data.status) {
    Swal.fire({
        title: 'Approved!',
        text: 'Guest request approved successfully. Do you want to allocate a room to this guest?',
        icon: 'success',
        showCancelButton: true,
        confirmButtonText: 'Yes, allocate room',
        cancelButtonText: 'No'
    }).then((result) => {
        if (result.isConfirmed) {
            // navigate to intake form (replace 47 with dynamic guest_id if available)
            window.location.href = `/admin/guest-management/guest-intake-form-move-in/${res.data.guest_id}`;
        } else {
            getGuestRequests();
        }
    });
} else {
    Swal.fire('Error!', res.data.message, 'error');
}
        } catch (error) {
            globalErrorHandler(error, navigate);
        }
    };

    const handleRemove = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'This request will be deleted!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await axios.delete(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/delete-guest-request/${id}`, { withCredentials: true });
                    if (res.data.status) {
                        Swal.fire('Deleted!', 'Guest request deleted successfully.', 'success');
                        getGuestRequests();
                    } else {
                        Swal.fire('Error!', res.data.message, 'error');
                    }
                } catch (error) {
                    globalErrorHandler(error, navigate);
                }
            }
        });
    };

    return (
        <div className="container-fluid px-4 mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>Guest Requests</h3>
            </div>

            <div className="card shadow mt-4">
               
                <div className="card-body p-4">
                    {/* Filter Section */}
                    <div className="row align-items-end g-2 mb-4">
                        <div className="col-md-4">
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control ps-3 pe-5 rounded-3"
                                    placeholder="Search by Name"
                                    name="name"
                                    value={filter.name}
                                    onChange={handleFilterChange}
                                />
                                <i
                                    className="bi bi-search position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                    style={{ pointerEvents: 'none' }}
                                ></i>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <button type="button" className="btn btn-dark w-100 rounded-3" onClick={clearFilter}>
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Guest Requests Table */}
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover shadow-sm nowrap">
                            <thead className="table-success">
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>SSN</th>
                                    <th>DOB</th>
                                    <th>Officer Name</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="8">Loading...</td></tr>
                                ) : guestRequests.length === 0 ? (
                                    <tr><td colSpan="8">No data found.</td></tr>
                                ) : (
                                    guestRequests.map(g => (
                                        <tr key={g.id}>
                                            <td>{g.id}</td>
                                            <td>{g.name}</td>
                                            <td>{g.email}</td>
                                            <td>{g.phone}</td>
                                            <td>{g.ssn}</td>
                                            <td>{g.dob ? new Date(g.dob).toLocaleDateString() : ''}</td>
                                            <td>{g.officer_name}</td>
                                            <td>
                                                <Button variant="success" size="sm" className="me-2" onClick={() => handleApprove(g.id)}>Approve</Button>
                                                <Button variant="danger" size="sm" onClick={() => handleRemove(g.id)}>Remove</Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-end mt-3">
                        <nav>
                            <ul className="pagination">
                                {[...Array(pagination.totalPages)].map((_, i) => (
                                    <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                                        >
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GuestRequests;
